package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * W7 참여 코드 + 가입 신청 큐(`functions/src/join.ts`).
 *
 * 학원 **개설** 신청([AcademyApplication], SUPER_ADMIN 승인)과 다른 도메인이다 — 여기는
 * 이미 운영 중인 학원에 회원이 **가입**을 신청하고, 그 학원의 스태프(EDITOR 이상)가
 * 승인한다. v1은 학생 전용이며 역할은 서버가 STUDENT로 고정한다. 보호자는 초대 링크
 * 경로를 유지한다(§2.6) — 보호자는 `members/{uid}` 문서를 받지 않아 멤버십을 만드는
 * 셀프서비스 큐로는 연결될 수 없다.
 */

/**
 * 참여 코드 확인 결과 상태. 초대 프리뷰([InvitationPreviewStatus])보다 의도적으로 작다 —
 * 참여 코드는 게시판에 붙는 다회용 코드라 "이미 사용됨"이라는 상태 자체가 없고,
 * 존재하지 않음·회수됨·학원 폐쇄를 전부 [UNUSABLE] 하나로 접어 코드 열거를 막는다.
 */
@Serializable
enum class JoinCodePreviewStatus {
    /** 지금 이 코드로 가입 신청을 제출할 수 있다. */
    USABLE,

    /** 존재하지 않음·회수됨·학원 비활성을 같은 결과로 접는다. */
    UNUSABLE,
}

/**
 * 참여 코드 발급·회전 결과(`issueAcademyJoinCode`/`rotateAcademyJoinCode`, ADMIN 전용).
 *
 * 발급은 멱등이다 — 설정 화면을 두 번 열어도 게시판에 이미 붙여 둔 코드가 무효화되지
 * 않는다. 그 경우 [replayed]가 참이고 기존 코드가 그대로 온다. 코드를 갈아치우는 것은
 * 회전뿐이며, 그때 회수된 이전 코드 수가 [revokedCount]로 온다.
 */
@Serializable
data class JoinCodeResult(
    /** 8자리 Crockford 계열(O/I/L/U 제외) — 구두로 불러 줘도 오독되지 않는다. */
    val code: String? = null,
    val academyId: String? = null,
    val academyName: String? = null,
    val createdAt: String? = null,
    /** 발급 호출이 기존 ACTIVE 코드를 재사용했다(멱등). 회전 결과에서는 항상 거짓이다. */
    val replayed: Boolean = false,
    /** 회전이 회수한 이전 코드 수. 발급 결과에서는 항상 0이다. */
    val revokedCount: Int = 0,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 참여 코드 확인의 비소비 결과(`previewAcademyJoinCode`, 로그인 필수·시간당 예산 공유).
 *
 * 답하는 질문은 하나다: "이 코드가 지금 어느 학원을 여는가". 호출자의 기존 신청·차단
 * 상태는 의도적으로 싣지 않는다 — 프리뷰에 그걸 접으면 차단된 사람에게 탐침을 쥐여 준다.
 */
@Serializable
data class JoinCodePreviewResult(
    val status: JoinCodePreviewStatus = JoinCodePreviewStatus.UNUSABLE,
    val academyId: String? = null,
    /** 학원 문서의 현재 이름 — 개명한 학원이 옛 이름으로 자기소개하지 않는다. */
    val academyName: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 가입 신청 상태기계. 신청자당 행 하나를 재사용한다 — 시도마다 새 문서를 만들면
 * 반려당한 신청자가 재제출로 스태프 큐를 파묻을 수 있다.
 *
 * ```
 * (없음) ──submit──▶ PENDING ──approve──▶ APPROVED (종결)
 *                    │ ▲ │──reject──▶ REJECTED ──24h 후 submit──▶ PENDING
 *      replayed ◀────┘ │ └──reject(blockFuture)──▶ BLOCKED ──unblock──▶ REJECTED
 *                      └──cancel──▶ CANCELLED ──24h 후 submit──▶ PENDING
 * ```
 */
@Serializable
enum class JoinApplicationStatus(val displayName: String) {
    PENDING("승인 대기"),
    APPROVED("승인됨"),
    REJECTED("반려됨"),
    CANCELLED("취소됨"),
    BLOCKED("차단됨"),
}

/**
 * 가입 신청 제출 결과(`submitAcademyJoinApplication`).
 *
 * [replayed]는 이미 PENDING인 행에 다시 제출한 경우다 — 쓰기 없이 기존 행이 그대로
 * 돌아오며 실패가 아니다. 반려·취소 후 24시간 안의 재제출은 `RESOURCE_EXHAUSTED`,
 * 차단된 신청자는 `FAILED_PRECONDITION`으로 온다(사유 비노출 일반 문구).
 */
@Serializable
data class SubmitJoinApplicationResult(
    val academyId: String? = null,
    val academyName: String? = null,
    val status: JoinApplicationStatus? = null,
    val replayed: Boolean = false,
    val attemptCount: Int = 0,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 가입 신청 한 행. 정본(`edubridge_academies/{id}/joinApplications/{uid}`, 스태프 read)과
 * 본인 미러(`edubridge_users/{uid}/joinApplications/{academyId}`)가 같은 payload를 쓴다.
 *
 * [applicantName]·[applicantEmail]은 동의 체크박스가 약속한 최소 PII 스냅샷이다 —
 * 큐 화면은 이 둘만 렌더하고, 문서 id가 uid라는 사실을 화면에 노출하지 않는다.
 */
@Serializable
data class JoinApplication(
    val academyId: String,
    /** 발급 시점 스냅샷. 본인 미러 표시용 — 스태프 화면은 자기 학원이라 쓸 일이 없다. */
    val academyName: String,
    val applicantUid: String,
    val applicantName: String? = null,
    val applicantEmail: String? = null,
    val status: JoinApplicationStatus,
    /** 재신청마다 +1. 2 이상이면 반려·취소를 거쳐 되돌아온 신청이다. */
    val attemptCount: Int = 1,
    /** "내 이름·이메일이 학원에 전달됩니다" 동의 시각(ISO-8601). */
    val consentAgreedAt: String? = null,
    /** 최근 제출 시각. 큐 정렬 키다(내림차순). */
    val submittedAt: String,
    val firstSubmittedAt: String? = null,
    val decidedBy: String? = null,
    /** 반려·차단·해제·취소가 갱신한다 — 24시간 쿨다운의 기준점. */
    val decidedAt: String? = null,
    /** 승인 시 연결·생성된 학생 문서 id. */
    val studentId: String? = null,
    val updatedAt: String? = null,
) {
    val isPending: Boolean get() = status == JoinApplicationStatus.PENDING
}
